/**
 * What every New sheet shares: what it was opened with (§9.2 — a thread, a
 * stop, a free slot, a list), the set-up the server answers once per sheet,
 * where it is saved (the record composer's endpoint on the first link, or
 * the team-items endpoint when nothing is attached — §9.5, §9.6), and the
 * words for where a sheet came from.
 */
import { useEffect, useState } from 'react'
import type { ComposeContact, NewSetup, RecordRef } from '@rowted/shared'
import { activityRequest } from '../api.js'
import { suggestFor, withChips, type AttachChip, type AttachType } from './attach.js'

/** What a sheet starts from. Every key is optional; anything else in the object is ignored. */
export interface NewPrefill {
  /** *To* (a text, an email) or *With* (a meeting). */
  contactId?: string
  /** The place's own chips — a thread's, a stop's. Never *auto*. */
  links?: { type: string; id: string; label: string }[]
  /**
   * The links are exactly what was picked — the quick card's *More options*,
   * whose *Attached to* already brought what comes along and may have had
   * some of it taken off — so nothing is added to them again.
   */
  linksExact?: boolean
  /** Opened from a thread (§9.2). */
  fromConversationId?: string
  /** Opened from a stop: the visit or meeting (§10.3, §11). */
  fromActivityId?: string
  /** A meeting's starting point — Board 6 D's *More options*, Board 18's free time. */
  title?: string
  date?: string
  start?: number
  minutes?: number
  people?: string[]
  contact?: { id: string; label: string } | null
  place?: { id: string; label: string } | null
  /** Edit an existing task or meeting instead of making one (*Reschedule*, *Move*). */
  editId?: string
  /**
   * With `editId`: one occurrence of a repeating meeting, by its own day in
   * the series — *Edit › Just this one* (Aaron, Oct 27, answer 12: "it should
   * be able to change pretty much anything").
   */
  occurrence?: string
  /** Opened from a task list (Board 16's *Task list* appears only then, §9.8). */
  taskListId?: string
  bucketId?: string
  /** Opened from a thread's *Make → Log a call* (§11.5). */
  logCall?: boolean
  /** Opened from a record page's *Email* or *Text* (Aaron, Oct 23): the message is filed on it. */
  origin?: { type: string; id: string }
  /**
   * Words to start a text's or an email's message (and an email's subject)
   * with — the calendar's *Let Renee know?* after a move (Aaron, Oct 24, note
   * 1.8). Only a start: nothing goes until **Send**.
   */
  body?: string
  subject?: string
}

export function readPrefill(raw: Record<string, unknown> | null | undefined): NewPrefill {
  const value = raw ?? {}
  const str = (key: string): string | undefined => (typeof value[key] === 'string' ? value[key] as string : undefined)
  const num = (key: string): number | undefined => (typeof value[key] === 'number' ? value[key] as number : undefined)
  const ref = (key: string): { id: string; label: string } | null => {
    const one = value[key] as { id?: unknown; label?: unknown } | null | undefined
    return one && typeof one.id === 'string' ? { id: one.id, label: typeof one.label === 'string' ? one.label : '' } : null
  }
  const links = Array.isArray(value.links)
    ? (value.links as { type?: unknown; id?: unknown; label?: unknown }[])
      .filter((link) => typeof link.type === 'string' && typeof link.id === 'string')
      .map((link) => ({ type: link.type as string, id: link.id as string, label: typeof link.label === 'string' ? link.label : '' }))
    : undefined
  return {
    contactId: str('contactId'),
    links,
    fromConversationId: str('fromConversationId'),
    fromActivityId: str('fromActivityId'),
    title: str('title'),
    date: str('date'),
    start: num('start'),
    minutes: num('minutes'),
    people: Array.isArray(value.people) ? (value.people as unknown[]).filter((one): one is string => typeof one === 'string') : undefined,
    contact: ref('contact'),
    place: ref('place'),
    editId: str('editId'),
    occurrence: str('occurrence'),
    taskListId: str('taskListId'),
    bucketId: str('bucketId'),
    logCall: value.logCall === true,
    body: str('body'),
    subject: str('subject'),
    linksExact: value.linksExact === true,
    origin: ref('origin') && typeof (value.origin as { type?: unknown }).type === 'string'
      ? { type: (value.origin as { type: string }).type, id: ref('origin')!.id }
      : undefined,
  }
}

const ATTACHABLE = new Set<string>(['job', 'contact', 'property', 'visit', 'invoice', 'equipment'])

/** The prefill's own chips, and what each of them brings that is not there yet (marked *auto*, Board 16 E). */
export function usePrefillChips(prefill: NewPrefill): [AttachChip[], (chips: AttachChip[]) => void] {
  const own: AttachChip[] = (prefill.links ?? [])
    .filter((link) => ATTACHABLE.has(link.type))
    /* A job's chip is its number (Board 11 D, F: *J-3319*), as the picker makes it; a thread's link carries the title too. */
    .map((link) => ({ type: link.type as AttachType, id: link.id, label: link.type === 'job' ? link.label.split(' · ')[0]! : link.label, auto: false }))
  if (prefill.place && !own.some((chip) => chip.type === 'property' && chip.id === prefill.place!.id)) {
    own.push({ type: 'property', id: prefill.place.id, label: prefill.place.label, auto: false })
  }
  if (prefill.contact && !own.some((chip) => chip.type === 'contact' && chip.id === prefill.contact!.id)) {
    own.push({ type: 'contact', id: prefill.contact.id, label: prefill.contact.label, auto: false })
  }
  const [chips, setChips] = useState<AttachChip[]>(own)
  useEffect(() => {
    if (own.length === 0 || prefill.linksExact) return
    let live = true
    void Promise.all(own.map((chip) => suggestFor(chip.type, chip.id))).then((lists) => {
      if (!live) return
      setChips((was) => withChips(was, lists.flat()))
    })
    return () => { live = false }
    // Once, for what the sheet was opened with.
  }, [])
  return [chips, setChips]
}

/** The set-up every sheet opens with (`GET /activity/new-setup`). */
export function useSetup(): NewSetup | null {
  const [setup, setSetup] = useState<NewSetup | null>(null)
  useEffect(() => {
    let live = true
    activityRequest<NewSetup>('/activity/new-setup')
      .then((answer) => { if (live) setSetup(answer) })
      .catch(() => { /* The sheet still works; the from lines wait. */ })
    return () => { live = false }
  }, [])
  return setup
}

/** A contact for *To* or *With* (`GET /activity/compose`). */
export function useContact(contactId: string | null): ComposeContact | null {
  const [contact, setContact] = useState<ComposeContact | null>(null)
  useEffect(() => {
    if (!contactId) { setContact(null); return undefined }
    let live = true
    activityRequest<ComposeContact>(`/activity/compose?contactId=${contactId}`)
      .then((answer) => { if (live) setContact(answer) })
      .catch(() => { if (live) setContact(null) })
    return () => { live = false }
  }, [contactId])
  return contact
}

/** The chips as the request sends them. */
export const refsOf = (chips: readonly AttachChip[]): RecordRef[] =>
  chips.map((chip) => ({ type: chip.type, id: chip.id }) as RecordRef)

/**
 * Make an activity: written on the first link through the record composer's
 * own endpoint, with the rest as links and `expand: false` so a removed chip
 * stays removed (§9.2); with nothing attached, the team-items endpoint (§4.1).
 */
export async function makeActivity(body: Record<string, unknown>, chips: readonly AttachChip[]): Promise<{ id: string }> {
  const refs = refsOf(chips)
  if (refs.length === 0) {
    return activityRequest<{ id: string }>('/activity/team-items', { method: 'POST', body })
  }
  const [first, ...rest] = refs
  return activityRequest<{ id: string }>(`/records/${first!.type}/${first!.id}/activities`, {
    method: 'POST', body: { ...body, links: rest, expand: false },
  })
}

const NUMBER_WORDS = ['no', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve']

/** *five* — Board 20 A spells a small count out. */
export const numberWords = (n: number): string => NUMBER_WORDS[n] ?? String(n)

/**
 * Where the sheet came from, under *Attached to* (§9.2): *opened from Dana
 * Okafor’s thread, so it’s attached the same way* (Board 11 D) · *opened from
 * Dana Okafor’s thread* (Board 11 F) · *opened from Joon Kim’s thread, so
 * it’s already attached* (Board 16 E).
 */
export function openedFromWords(input: {
  name: string | null; kind: 'thread' | 'stop'; required: boolean; phone: boolean
}): string | null {
  if (!input.name) return null
  const from = input.kind === 'thread' ? `opened from ${input.name}’s thread` : `opened from ${input.name}`
  if (input.phone) return `${from}, so it’s already attached`
  return input.required ? `${from}, so it’s attached the same way` : from
}

/** A failure as the sheet says it. */
export const problemOf = (cause: unknown): string =>
  (cause instanceof Error ? cause.message : String(cause)) || 'That didn’t save. Try again.'
